import { FC, useEffect, useState, useMemo, useDeferredValue } from "react";
import moment from "moment";
import { useApiCall } from "../../../../lib/src/api";
import { config } from "../../config";
import { useFetchedResource } from "../../../../lib/src/util/useFetchedResource";
import { NotificationLog } from "../../../../lib/src/api/repositories/notification-logs-repository";
import { Badge, BadgeStyle } from "../../components/Badge";
import { Button } from "@jane/lib/src/components/form/Button";
import { faArchive, faCircleXmark } from "@fortawesome/pro-regular-svg-icons";
import { useToasts } from "@jane/lib/src/toasts/toasts";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Input } from "@jane/lib/src/components/form/Input";

export const NotificationLogs = () => {
  const { notificationLogs: notificationLogsService } = useApiCall(config);
  const { showToast } = useToasts();
  const [selected, setSelected] = useState<string | null>(null);
  const notificationLogs = useFetchedResource<NotificationLog[]>(() => notificationLogsService.browse());
  useEffect(() => {
    notificationLogs.reload(undefined);
  }, []);

  const selectedLog = notificationLogs.resource?.find((l) => l.id === selected);
  const [search, setSearch] = useState("");
  const deferredSearch = useDeferredValue(search);
  const filteredNotificationLogs = useMemo(
    () => notificationLogs.resource?.filter((x) => x.receiverName.startsWith(search)),
    [deferredSearch, notificationLogs],
  );

  return (
    <div className={"flex-1 flex flex-col -mx-6 -mt-8 -mb-8"}>
      <div className={"h-20 bg-white border-b border-slate-200 px-6 flex items-center"}>
        <div className={"text-2xl font-bold mr-6"}>Postvak-uit</div>
        <div className={"text-slate-600 text-sm mt-1"}>Verstuurde berichten vanuit MiepApp</div>
      </div>
      <div className={"flex flex-col items-stretch min-w-64 max-w-lg border-r border-slate-200 bg-white"}>
        <Input
          type={"text"}
          label={""}
          value={search}
          maxWidth={"full"}
          onChange={(v: string) => {
            setSearch(v);
          }}
        />
        <div className={"overflow-y-scroll h-[calc(100vh-13rem)]"}>
          {notificationLogs.loading ? (
            <>
              <div className={"h-24 mx-3 my-4 bg-slate-200 rounded-lg animate-pulse"}></div>
              <div className={"h-24 mx-3 my-4 bg-slate-200 rounded-lg animate-pulse"}></div>
            </>
          ) : (
            filteredNotificationLogs?.map((log, i) => {
              const isSelected = log.id === selected;
              return (
                <div
                  key={i}
                  onClick={() => setSelected(log.id)}
                  className={`h-24 px-3 py-2 mx-3 my-4 rounded-lg cursor-pointer border ${isSelected ? "bg-white border-blue-200" : "border-transparent hover:border-slate-200"}  `}
                >
                  <div className={"h-6 flex items-center"}>
                    <div className={"flex-1 font-medium truncate mr-2"}>{log.receiverName}</div>
                    <NotificationLogStatusBadge notificationLog={log} />
                  </div>
                  <div className={"h-6 text-sm truncate"}>{log.title}</div>
                  <div className={"max-h-12 text-slate-500 text-xs line-clamp-2"}>{log.preview}</div>
                </div>
              );
            })
          )}
        </div>
        <div className={"flex-1 bg-brand-surface px-8"}>
          {selectedLog && (
            <div className={"max-w-2xl mx-auto bg-white flex flex-col space-y-4 py-4 px-6 rounded-lg my-10 shadow-lg"}>
              <div className={"flex items-center"}>
                <div className={"flex-1 font-medium mr-2"}>{selectedLog.receiverName}</div>
                <NotificationLogStatusBadge notificationLog={selectedLog} />
              </div>
              <div className={"text-sm"}>{selectedLog.title}</div>
              <pre className={"text-wrap font-sans -mx-6 border-t border-slate-200 pt-6 pb-2 px-6 relative"}>
                {selectedLog.preview}
                <div className={"absolute inset-x-0 bottom-0 h-12 bg-linear-to-t from-white to-transparent"}></div>
              </pre>

              <div className={"border-t border-slate-200 -mx-6 px-6 pt-3"}>
                <div className={"h-12 flex items-center"}>
                  <div className={"text-slate-600 italic text-sm flex-1"}>
                    Aangemaakt:{" "}
                    <strong className={"font-medium"}>
                      {moment(selectedLog.created_at).format("DD-MM-YYYY HH:mm")}
                    </strong>
                    , Geüpdate:{" "}
                    <strong className={"font-medium"}>
                      {moment(selectedLog.updated_at).format("DD-MM-YYYY HH:mm")}
                    </strong>
                  </div>
                  <Button
                    type={"secondary"}
                    size={"xs"}
                    text={"Archiveren"}
                    icon={faArchive}
                    onClick={async () => {
                      await notificationLogsService
                        .delete(selectedLog.id)
                        .then(() => {
                          notificationLogs.reload();
                          showToast("Log gearchiveerd", "Registratie opgeschoond.");
                        })
                        .catch(() => {
                          showToast("Error", "Kon niet archiveren.");
                        });
                    }}
                  />
                </div>

                {selectedLog.reason && (
                  <pre className={"text-red-700 mt-4 text-center"}>
                    <FontAwesomeIcon icon={faCircleXmark} className="mr-1" /> {selectedLog.reason}
                  </pre>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export const NotificationLogStatusBadge: FC<{ notificationLog: NotificationLog }> = (props) => {
  const styleMapping = {
    pending: BadgeStyle.default,
    sent: BadgeStyle.info,
    success: BadgeStyle.success,
    failed: BadgeStyle.danger,
  }[props.notificationLog.status];
  const textMapping = {
    pending: "Versturen...",
    sent: "Verzonden",
    success: "Afgeleverd",
    failed: "Afleverfout",
  }[props.notificationLog.status];

  return <Badge style={styleMapping}>{textMapping}</Badge>;
};

